import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';
import IndeterminateCheckbox from 'components/common/advance-table/IndeterminateCheckbox';

import { getOrderLineBadgeColor } from 'constants/order';
import { getShipmentLineBadgeColor } from 'constants/shipment';
// Removed channelApi usage; we now rely on detail prop `orderCancelSyncEnabled`

const OrderCancelModal = ({
  show,
  onHide,
  orderLines,
  onConfirm,
  channelId,
  warehouseId,
  // New prop coming from order detail: boolean
  orderCancelSyncEnabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedLines, setSelectedLines] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [refundEnabled, setRefundEnabled] = useState(false);
  // No external loading of channel setting anymore

  useEffect(() => {
    if (!show) {
      setSelectedLines(new Set());
      setExpandedRows(new Set());
      setRefundEnabled(false);
    }
  }, [show]);

  // Initialize refund checkbox based on orderCancelSyncEnabled when modal opens
  useEffect(() => {
    if (show) {
      setRefundEnabled(!!orderCancelSyncEnabled);
    }
  }, [show, orderCancelSyncEnabled]);

  const handleSelectAll = checked => {
    if (checked) {
      const allLines = new Set();

      orderLines.forEach(orderLine => {
        orderLine.shipment_lines?.forEach(line => {
          if (line.is_cancelable) {
            allLines.add(line.id);
          }
        });
      });
      setSelectedLines(allLines);
    } else {
      setSelectedLines(new Set());
    }
  };

  const handleOrderLineSelect = (shipmentLines, checked) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      shipmentLines.forEach(line => {
        if (line.is_cancelable) {
          if (checked) {
            next.add(line.id);
          } else {
            next.delete(line.id);
          }
        }
      });
      return next;
    });
  };

  const handleShipmentLineSelect = (lineId, checked) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(lineId);
      } else {
        next.delete(lineId);
      }
      return next;
    });
  };

  const handleRefundToggle = e => {
    setRefundEnabled(e.target.checked);
  };

  const handleConfirm = async () => {
    if (selectedLines.size === 0) return;

    setLoading(true);
    try {
      await onConfirm(Array.from(selectedLines), refundEnabled);
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpand = orderLineId => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(orderLineId)) {
        next.delete(orderLineId);
      } else {
        next.add(orderLineId);
      }
      return next;
    });
  };

  const isOrderLineCancelable = shipmentLines => {
    return shipmentLines?.some(line => line.is_cancelable);
  };

  const areAllSelected = orderLines.every(orderLine => {
    const cancelableLines =
      orderLine.shipment_lines?.filter(line => line.is_cancelable) || [];
    return (
      cancelableLines.length === 0 ||
      cancelableLines.every(line => selectedLines.has(line.id))
    );
  });

  const isSomeSelected = selectedLines.size > 0;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Cancel Orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-responsive">
          <Table className="table-sm">
            <thead>
              <tr>
                <th className="text-nowrap" style={{ width: '40px' }}>
                  <IndeterminateCheckbox
                    checked={areAllSelected}
                    indeterminate={!areAllSelected && isSomeSelected}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th style={{ width: '40px' }}></th>
                <th>Product</th>
                <th>SKU</th>
                <th>Status</th>
                <th className="text-end">Price</th>
              </tr>
            </thead>
            <tbody>
              {orderLines.map(orderLine => {
                const cancelable = isOrderLineCancelable(
                  orderLine.shipment_lines,
                );
                const cancelableLines =
                  orderLine.shipment_lines?.filter(
                    line => line.is_cancelable,
                  ) || [];
                const allCancelableLinesSelected = cancelableLines.every(line =>
                  selectedLines.has(line.id),
                );
                const someLinesSelected = cancelableLines.some(line =>
                  selectedLines.has(line.id),
                );
                const sumOfUnitGrossAmount = orderLine.shipment_lines
                  .reduce((sum, line) => {
                    return sum + (parseFloat(line.unit_gross_amount) || 0);
                  }, 0)
                  .toFixed(2);

                return (
                  <React.Fragment key={orderLine.id}>
                    <tr
                      className={
                        expandedRows.has(orderLine.id) ? 'bg-light' : ''
                      }
                    >
                      <td>
                        <IndeterminateCheckbox
                          checked={allCancelableLinesSelected}
                          indeterminate={
                            !allCancelableLinesSelected && someLinesSelected
                          }
                          disabled={!cancelable}
                          onChange={e =>
                            handleOrderLineSelect(
                              orderLine.shipment_lines,
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <button
                          className="btn p-0"
                          onClick={() => toggleRowExpand(orderLine.id)}
                        >
                          <FontAwesomeIcon
                            icon={
                              expandedRows.has(orderLine.id)
                                ? 'chevron-down'
                                : 'chevron-right'
                            }
                            size="sm"
                          />
                        </button>
                      </td>
                      <td>{orderLine.channel_title}</td>
                      <td>{orderLine.channel_sku}</td>
                      <td>
                        <SubtleBadge
                          bg={getOrderLineBadgeColor(
                            orderLine.order_line_status,
                          )}
                        >
                          {['created', 'processing'].includes(
                            orderLine.order_line_status,
                          )
                            ? orderLine.channel_product_id === null
                              ? 'Channel Product Required'
                              : orderLine.product_id === null
                              ? 'Product Disconnected'
                              : orderLine.order_line_status_label
                            : orderLine.order_line_status_label}
                        </SubtleBadge>
                      </td>
                      <td className="text-end">₹{sumOfUnitGrossAmount}</td>
                    </tr>
                    {expandedRows.has(orderLine.id) && (
                      <tr>
                        <td colSpan="6" className="p-0">
                          <Table className="table-sm m-0">
                            <thead>
                              <tr>
                                <th
                                  className="ps-5"
                                  style={{ width: '40px' }}
                                ></th>
                                <th>Shipment Code</th>
                                <th>Product Name</th>
                                <th>Status</th>
                                <th className="text-end">Price</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {orderLine.shipment_lines?.map(line => (
                                <tr key={line.id}>
                                  <td
                                    className="ps-5"
                                    style={{ width: '40px' }}
                                  >
                                    <IndeterminateCheckbox
                                      checked={selectedLines.has(line.id)}
                                      disabled={!line.is_cancelable}
                                      onChange={e =>
                                        handleShipmentLineSelect(
                                          line.id,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>{line.shipment_code}</td>
                                  <td>{line.product?.name}</td>
                                  <td>
                                    <SubtleBadge
                                      bg={getShipmentLineBadgeColor(
                                        line.shipment_line_status,
                                      )}
                                    >
                                      {line.is_cancelable === true &&
                                      line.product_batch_id === null
                                        ? 'Out of Stock'
                                        : line.shipment_line_status_label}
                                    </SubtleBadge>
                                  </td>
                                  <td className="text-end">
                                    ₹{line.unit_gross_amount}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center gap-2">
            <label className="d-flex align-items-center gap-2 mb-0">
              <input
                type="checkbox"
                checked={refundEnabled}
                onChange={handleRefundToggle}
                disabled={!orderCancelSyncEnabled}
                className="form-check-input"
                style={{ marginTop: 0 }}
              />
              <span
                className={orderCancelSyncEnabled ? 'text' : 'text-muted'}
                style={{
                  lineHeight: '1',
                  opacity: orderCancelSyncEnabled ? 1 : 0.7,
                  fontSize: '0.875rem',
                }}
              >
                Process with Refund
              </span>
            </label>

            <small
              className={`text-${
                orderCancelSyncEnabled ? 'info' : 'danger'
              } mb-0`}
              style={{ lineHeight: '1' }}
            >
              {orderCancelSyncEnabled
                ? 'Order cancel sync is turned on for this channel.'
                : 'Order cancel sync is turned off for this channel.'}
            </small>
          </div>

          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Close
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={selectedLines.size === 0 || loading}
            >
              {loading ? 'Cancelling...' : `Cancel (${selectedLines.size})`}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderCancelModal;
